import { useContext, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";
import { db } from "../firebase";

export default function Onboarding() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { translate } = useContext(LanguageContext);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  const steps = useMemo(
    () => [
      {
        key: "acquisitionSource",
        question: translate("OnboardingQuestionAcquisition"),
        options: [
          { value: "google", label: translate("OnboardingOptionGoogle") },
          { value: "chatgpt", label: translate("OnboardingOptionChatgpt") },
          {
            value: "reddit_similar",
            label: translate("OnboardingOptionReddit"),
          },
          { value: "other", label: translate("OnboardingOptionOther") },
        ],
      },
      {
        key: "usageType",
        question: translate("OnboardingQuestionUsage"),
        options: [
          { value: "pro", label: translate("OnboardingOptionPro") },
          {
            value: "personal",
            label: translate("OnboardingOptionPersonal"),
          },
        ],
      },
      {
        key: "industryGroup",
        question: translate("OnboardingQuestionIndustry"),
        options: [
          {
            value: "tech_saas_agency",
            label: translate("OnboardingOptionTech"),
          },
          {
            value: "ecommerce_retail_online",
            label: translate("OnboardingOptionEcommerce"),
          },
          {
            value: "local_services_sme",
            label: translate("OnboardingOptionLocal"),
          },
          {
            value: "other",
            label: translate("OnboardingOptionOtherIndustry"),
          },
        ],
      },
    ],
    [translate]
  );

  const step = steps[stepIndex];
  const selectedValue = answers[step.key] || "";
  const isLastStep = stepIndex === steps.length - 1;

  const handleSelect = (value) => {
    setAnswers((prev) => ({ ...prev, [step.key]: value }));
  };

  const handleNext = async () => {
    if (!selectedValue) return;
    if (!isLastStep) {
      setStepIndex((prev) => prev + 1);
      return;
    }

    if (!currentUser?.uid) return;
    setSaving(true);
    try {
      await setDoc(
        doc(db, "profilsUsers", currentUser.uid),
        {
          onboardingCompleted: true,
          onboardingAnswers: {
            acquisitionSource: answers.acquisitionSource || "",
            usageType: answers.usageType || "",
            industryGroup: answers.industryGroup || "",
          },
          onboardingCompletedAt: serverTimestamp(),
        },
        { merge: true }
      );
      navigate("/app", { replace: true });
    } catch (error) {
      console.error("Onboarding save failed:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Paper sx={{ width: "100%", maxWidth: 760, p: { xs: 2.5, md: 4 } }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {translate("OnboardingTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {translate("OnboardingSubtitle")}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, display: "block" }}
        >
          {translate("OnboardingStepLabel")} {stepIndex + 1}/{steps.length}
        </Typography>

        <Typography variant="h6" sx={{ mt: 1.5, mb: 2, fontWeight: 600 }}>
          {step.question}
        </Typography>

        <Stack spacing={1.25}>
          {step.options.map((option) => {
            const selected = selectedValue === option.value;
            return (
              <Button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                variant={selected ? "contained" : "outlined"}
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  py: 1.3,
                  px: 1.8,
                }}
              >
                {option.label}
              </Button>
            );
          })}
        </Stack>

        <Box
          sx={{ display: "flex", gap: 1, mt: 3, justifyContent: "space-between" }}
        >
          <Button
            variant="text"
            onClick={() => setStepIndex((prev) => Math.max(prev - 1, 0))}
            disabled={stepIndex === 0 || saving}
          >
            {translate("OnboardingBack")}
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!selectedValue || saving}
          >
            {saving ? (
              <CircularProgress size={18} color="inherit" />
            ) : isLastStep ? (
              translate("OnboardingFinish")
            ) : (
              translate("OnboardingNext")
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
